import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  getWorkspaceData,
  redeemWorkspaceInviteToken,
} from "@services/api";
import { setWorkspaceData } from "@store/slices/workspaceSlice";
import { useNotification } from "@shared/contexts/NotificationContext";

// Join landing page for workspace invite links. Mirrors OrgJoinScreen:
//
//   1. Signed in → redeem immediately, switch the user into the new
//      workspace, navigate to /annogram.
//   2. Signed out → stash the join URL in sessionStorage and bounce to
//      the marketing landing; the AuthenticationModal honors the
//      stored redirect after sign-in/sign-up brings the user back here.
//
// Tokens are validated server-side (expired/revoked/missing return
// distinct status codes); we surface whatever the server says.
const WorkspaceJoinScreen = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userId } = useAppSelector((state) => state.user);
  const { showSuccess, showError } = useNotification();
  const [status, setStatus] = useState<"idle" | "redeeming" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) return;

    if (!userId) {
      sessionStorage.setItem("postAuthRedirect", `/workspaces/join/${token}`);
      navigate("/");
      return;
    }

    if (status !== "idle") return;
    setStatus("redeeming");

    (async () => {
      const result = await redeemWorkspaceInviteToken(token);
      if (!result.success) {
        setStatus("error");
        setErrorMessage(result.error.message);
        showError(result.error.message);
        return;
      }

      // Fetch the workspace fully (with permissions + categories) so the
      // global context switches cleanly. The redeem response carries only
      // id + name to keep the public-ish surface minimal.
      const wsResult = await getWorkspaceData(result.data.workspace_id);
      if (wsResult.success) {
        const ws = wsResult.data;
        dispatch(
          setWorkspaceData({
            workspace_id: ws.workspace_id,
            workspace_name: ws.workspace_name,
            shared_data: ws.shared_data,
            user_data: ws.user_data,
            workspace_permissions: ws.workspace_permissions,
          })
        );
      }

      showSuccess(`Joined ${result.data.workspace_name}`);
      navigate("/annogram");
    })();
  }, [token, userId, status, navigate, dispatch, showError, showSuccess]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
        {status === "error" ? (
          <>
            <Typography variant="h5" gutterBottom>
              Couldn't join the workspace
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {errorMessage || "The invite link is no longer valid."}
            </Typography>
            <Button variant="outlined" onClick={() => navigate("/annogram")}>
              Continue to ArgFinder
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              Joining workspace…
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {token}
              </Box>
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default WorkspaceJoinScreen;
